/**
 * 极简「取数三态」hook：loading / ready / error + reload。
 *
 * 刻意不引入 TanStack Query 的缓存层：本轮只需要「进入页面取一次、可重试」，
 * 而三态语义（loading 不闪、error 不吞、empty 单独表达）与 StateView 一一对应。
 * 请求可被 AbortSignal 取消，避免组件卸载后的 setState。
 */

import { useCallback, useEffect, useRef, useState } from "react";

export type ResourceState = "loading" | "ready" | "error";

export interface Resource<T> {
  readonly data: T | null;
  readonly state: ResourceState;
  readonly error: unknown;
  readonly reload: () => void;
}

export function useApiResource<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
): Resource<T> {
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const [data, setData] = useState<T | null>(null);
  const [state, setState] = useState<ResourceState>("loading");
  const [error, setError] = useState<unknown>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setState("loading");
    setError(null);
    loaderRef
      .current(controller.signal)
      .then((next) => {
        if (cancelled) return;
        setData(next);
        setState("ready");
      })
      .catch((cause: unknown) => {
        if (cancelled || controller.signal.aborted) return;
        setError(cause);
        setState("error");
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
    // deps 由调用方保证稳定（原始值数组）
  }, [...deps, version]);

  const reload = useCallback(() => setVersion((value) => value + 1), []);
  return { data, state, error, reload };
}

/**
 * 把任意异常转成可展示的一行文本（**保留 code 方便排障**）。
 *
 * C1 Phase 3（2026-10-06）：**403 统一文案** —— 权限不足时不再把原始 code 当正文
 * （用户口径：不要显示原始错误码），改为「权限不足 + 指引联系管理员」。
 * 功能名由调用方按页面 feature 补充（见 `lib/permissions.ts` / `PermissionDenied.tsx`）。
 */
export function describeError(error: unknown): string {
  if (error === null || error === undefined) return "未知错误";
  if (typeof error === "object") {
    const code = (error as { code?: unknown }).code;
    const message = (error as { message?: unknown }).message;
    const httpStatus = (error as { httpStatus?: unknown }).httpStatus;
    if (httpStatus === 403 || code === "FORBIDDEN") {
      return "权限不足：请联系管理员确认你的账号是否具备该功能的权限。";
    }
    if (typeof code === "string" && typeof message === "string") return `${code}：${message}`;
  }
  if (error instanceof Error) return error.message;
  return String(error);
}
